import { Dispatch } from 'react';
import api from '~/services';
import { MessageTypeEnum } from '~/enums/conversations';
import * as actionTypes from './actionTypes';

export type ModerationStatus = 'pending' | 'approved' | 'rejected';
export type MimeType = 'image' | 'video' | 'audio';

export interface MmsUploadResult {
  mediaId: string;
  url: string;
  type: MimeType;
  messageType: MessageTypeEnum;
  size: number;
  moderationStatus: ModerationStatus;
}

/**
 * Uploads a single file to POST /v2/mms/media.
 * Each file must be uploaded individually. Call once per file.
 * Dispatches START → SUCCESS | FAILURE and resolves with the server response.
 */
export const uploadMmsMediaFile = (
  file: File,
  onProgress: (pct: number) => void
) => async (
  dispatch: Dispatch<{ type: string; payload?: unknown }>
): Promise<MmsUploadResult> => {
  dispatch({
    type: actionTypes.MMS_UPLOAD_MEDIA_START,
    payload: { fileName: file.name },
  });

  const formData = new FormData();
  formData.append('file', file);

  try {
    const { data } = await api.conversation.post.uploadMmsMediaFile(formData, {
      onUploadProgress: (event: ProgressEvent) => {
        if (event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    });

    dispatch({
      type: actionTypes.MMS_UPLOAD_MEDIA_SUCCESS,
      payload: { fileName: file.name },
    });

    return data as MmsUploadResult;
  } catch (err) {
    const message: string =
      // @ts-ignore
      err?.response?.data?.message ?? err?.message ?? 'Upload failed.';

    dispatch({
      type: actionTypes.MMS_UPLOAD_MEDIA_FAILURE,
      payload: { fileName: file.name, error: message },
    });

    throw new Error(message);
  }
};

export interface MmsMediaModerationState {
  mediaId: string;
  type: MimeType;
  moderationStatus: ModerationStatus;
  isApproved: boolean;
}

export type ModerationOutcome =
  | 'approved'
  | 'rejected'
  | 'unverifiable'
  | 'timeout';

export const getMmsMediaModerationStatus = async (
  mediaId: string
): Promise<MmsMediaModerationState> => {
  const { data } = await api.conversation.get.getMmsMediaModerationStatus(
    mediaId
  );

  return data as MmsMediaModerationState;
};

const MODERATION_POLL_DELAYS_MS = [
  1000,
  1500,
  2000,
  3000,
  3000,
  5000,
  5000,
  5000,
  5000,
  5000,
  5000,
  5000,
  10000,
  10000,
  10000,
];

const wait = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

export async function waitForMmsModeration(
  mediaId: string,
  isCancelled: () => boolean = () => false
): Promise<ModerationOutcome> {
  for (
    let attempt = 0;
    attempt < MODERATION_POLL_DELAYS_MS.length;
    attempt += 1
  ) {
    // eslint-disable-next-line no-await-in-loop
    await wait(MODERATION_POLL_DELAYS_MS[attempt]);

    if (isCancelled()) {
      return 'unverifiable';
    }

    try {
      // eslint-disable-next-line no-await-in-loop
      const state = await getMmsMediaModerationStatus(mediaId);

      if (isCancelled()) {
        return 'unverifiable';
      }

      if (state.moderationStatus === 'approved' || state.isApproved) {
        return 'approved';
      }

      if (state.moderationStatus === 'rejected') {
        return 'rejected';
      }
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;

      if (status === 403 || status === 404) {
        return 'unverifiable';
      }
    }
  }

  return 'timeout';
}
